import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'validate.js';

import { pushParams } from 'dpl/actions/query_params';
import Button from 'dpl/common/design_system/Button';
import OwnerStoriesSkeleton from 'dpl/components/BreederProfile/Show/skeletons/BreederProfileOwnerStoriesSkeleton';
import OwnerStory from 'dpl/components/OwnerStory';
import { breederProfileBreederTestimonialIndexResourceDefinition } from 'dpl/shared/breeder/utils/queryDefinitions';
import useInfiniteCollectionQuery from 'dpl/shared/fetching/hooks/useInfiniteCollectionQuery';
import { isGreaterThanBreakpoint } from 'dpl/util/grid';
import SectionViewTracker from 'dpl/common/components/SectionViewTracker';
import { TAB_KEYS } from 'dpl/components/BreederProfile/utils/constants';
import useStoriesLabel from 'dpl/common/hooks/useStoriesLabel';

export const SECTION_ID = 'stories-section';
export const STORIES_PER_PAGE = 6;

/** @param {any} props */
export default function BreederProfileOwnerStoriesContainer({
  className = null
}) {
  const {
    breederFirstName,
    breederProfileId,
    analyticsPageName,
    testimonialsCount
  } = useSelector(({ server }) => server);

  const { infiniteScrollItems, hasNextPage, isLoading, isFetching } =
    useInfiniteCollectionQuery(
      breederProfileBreederTestimonialIndexResourceDefinition(
        breederProfileId,
        {
          pageNumber: 1,
          perPage: STORIES_PER_PAGE
        }
      )
    );

  const dispatch = useDispatch();
  const { shortNoun } = useStoriesLabel(testimonialsCount);

  const columns = useMemo(() => {
    return infiniteScrollItems.reduce(
      (acc, story, index) => {
        const key = isGreaterThanBreakpoint('md') ? index % 2 : 0;
        acc[key].push(story);
        return acc;
      },
      [[], []]
    );
  }, [infiniteScrollItems]);

  if (isLoading) {
    return <OwnerStoriesSkeleton className={className} />;
  }

  if (!isLoading && infiniteScrollItems.length === 0) {
    return null;
  }

  return (
    <SectionViewTracker
      name={analyticsPageName}
      category="breeder-profile-owner-stories"
      properties={{ breeder_profile_id: breederProfileId }}
      rootMargin="-300px 0px"
      threshold={0}
    >
      <section
        id={SECTION_ID}
        className={classnames(
          'BreederProfileOwnerStoriesContainer container-xxl pv8 pv20-md overflow-hidden',
          className
        )}
      >
        <div className="mb10 mb12-md">
          <h2 className="font-24 md:font-40 lh-title mb2">
            Verified owner stories
          </h2>
          <p className="font-18 secondary">
            Hear what other owners say about working with {breederFirstName} to
            bring home their puppies.
          </p>
        </div>
        <ul className="grid grid-cols-1 lg:grid-cols-2 justify-between gap-96">
          {columns
            .filter(column => !isEmpty(column))
            .map((stories, colIndex) => (
              <li key={colIndex}>
                {stories.map((story, index) => (
                  <OwnerStory
                    key={story.id}
                    id={story.id}
                    className={classnames({
                      'pb6 pb12-md': index !== stories.length - 1,
                      'pt6 pt12-md bt b--default': index
                    })}
                    date={story.formatted_submitted_at}
                    description={story.experience}
                    gallery={story.photographs}
                    user={story.buyer_profile}
                    isExpanded={window.__DPL_BOT_UA}
                  />
                ))}
              </li>
            ))}
        </ul>
        {hasNextPage && (
          <Button
            className="font-16 stone-700 ph16 pv3 w-100 w-auto-m mt6"
            variant="outline"
            onClick={() => {
              dispatch(pushParams({ tab: TAB_KEYS.STORIES }, { hash: true }));
              window.scrollTo(0, 0);
            }}
            disabled={isFetching}
          >
            Show all {testimonialsCount} {shortNoun}
          </Button>
        )}
      </section>
    </SectionViewTracker>
  );
}

BreederProfileOwnerStoriesContainer.propTypes = {
  className: PropTypes.string
};
